
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, MapPin, Calendar, Users, ChevronLeft, ChevronRight,
  Star, BedDouble, Wifi, Coffee, Car, Waves, ArrowRight,
  Shield, Zap, Award, HeartHandshake, Compass,
} from 'lucide-react';
import './HomePage.css';

// ── Hero carousel images ────────────────────────────────────────────────────
const HERO_SLIDES = [
  { url: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1600&auto=format&fit=crop', title: 'Find Your Perfect Stay',   subtitle: 'Millions of great deals on hotels, flights, car rentals and more' },
  { url: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1600&auto=format&fit=crop', title: 'Luxury Awaits You',       subtitle: 'World-class resorts with breathtaking views and premium amenities' },
  { url: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1600&auto=format&fit=crop', title: 'Experience Elegance',      subtitle: 'Book with confidence — best price guaranteed on every property' },
  { url: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=1600&auto=format&fit=crop', title: 'Rest in Comfort',         subtitle: 'From boutique hotels to grand suites — your dream room is waiting' },
  { url: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1600&auto=format&fit=crop', title: 'Escape the Ordinary',    subtitle: 'Handpicked properties in the most sought-after destinations worldwide' },
];

// ── Popular destinations ────────────────────────────────────────────────────

// ── Attraction tab destinations ─────────────────────────────────────────────
const ATTR_DESTINATIONS = [
  { label: 'Dubai',     flag: '🇦🇪', query: 'Dubai, UAE' },
  { label: 'Paris',     flag: '🇫🇷', query: 'Paris, France' },
  { label: 'Tokyo',     flag: '🇯🇵', query: 'Tokyo, Japan' },
  { label: 'New York',  flag: '🇺🇸', query: 'New York, USA' },
  { label: 'Rome',      flag: '🇮🇹', query: 'Rome, Italy' },
  { label: 'Istanbul',  flag: '🇹🇷', query: 'Istanbul, Turkey' },
  { label: 'Maldives',  flag: '🇲🇻', query: 'Maldives' },
  { label: 'Bangkok',   flag: '🇹🇭', query: 'Bangkok, Thailand' },
];
const DESTINATIONS = [
  { name: 'Dubai',    country: 'UAE',       img: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=500&auto=format&fit=crop', tag: '1,240+ hotels' },
  { name: 'Paris',    country: 'France',    img: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=500&auto=format&fit=crop', tag: '2,800+ hotels' },
  { name: 'New York', country: 'USA',       img: 'https://images.unsplash.com/photo-1543716091-a840c05249ec?w=500&auto=format&fit=crop', tag: '3,100+ hotels' },
  { name: 'Tokyo',    country: 'Japan',     img: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=500&auto=format&fit=crop', tag: '1,870+ hotels' },
  { name: 'Maldives', country: 'Maldives',  img: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=500&auto=format&fit=crop', tag: '340+ resorts' },
  { name: 'Istanbul', country: 'Turkey',    img: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=500&auto=format&fit=crop', tag: '980+ hotels' },
  { name: 'London',   country: 'UK',        img: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=500&auto=format&fit=crop', tag: '2,500+ hotels' },
  { name: 'Bali',     country: 'Indonesia', img: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=500&auto=format&fit=crop', tag: '1,100+ villas' },
];

// ── Why InnBotics ──────────────────────────────────────────────────────────
const FEATURES = [
  { Icon: Zap,            color: '#febb02', title: 'AI-Powered Booking',  desc: 'Our smart concierge answers questions, suggests rooms, and guides you from search to check-in.' },
  { Icon: Shield,         color: '#003580', title: 'Secure & Trusted',    desc: 'Industry-grade encryption on every payment. Your data and money are always protected.' },
  { Icon: Award,          color: '#0ea5e9', title: 'Best Price Promise',  desc: 'We match any lower rate you find. Guaranteed. No hidden fees or surprises.' },
  { Icon: HeartHandshake, color: '#10b981', title: '24 / 7 Support',      desc: 'A real person or our AI is always on hand — before, during, and after your stay.' },
];

// ── Fallback hotel images ──────────────────────────────────────────────────
const HOTEL_IMGS = [
  'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=500&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=500&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=500&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=500&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=500&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=500&auto=format&fit=crop',
];

// ── Helpers ────────────────────────────────────────────────────────────────
function Stars({ count = 5 }) {
  const n = Math.round(Math.min(Math.max(count, 0), 5));
  return (
    <span className="hp-stars">
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} size={12} fill="#febb02" stroke="none" />
      ))}
    </span>
  );
}

function AmenityChip({ type }) {
  const icons = { wifi: <Wifi size={12} />, coffee: <Coffee size={12} />, parking: <Car size={12} />, pool: <Waves size={12} /> };
  const labels = { wifi: 'WiFi', coffee: 'Breakfast', parking: 'Parking', pool: 'Pool' };
  return (
    <span className="hp-amenity-chip">
      {icons[type]} {labels[type]}
    </span>
  );
}

function formatPrice(val) {
  const n = parseFloat(val);
  if (!n || isNaN(n)) return null;
  return `$${Math.round(n).toLocaleString()}`;
}

// ═══════════════════════════════════════════════════════════════════════════
export default function HomePage() {
  const navigate = useNavigate();

  // ── Search state ───────────────────────────────────────────────────────
  const today    = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const [destination, setDestination] = useState('');
  const [checkIn,  setCheckIn]  = useState(today);
  const [checkOut, setCheckOut] = useState(tomorrow);
  const [guests,   setGuests]   = useState(2);

  // ── Hero carousel ──────────────────────────────────────────────────────
  const [slide,     setSlide]     = useState(0);
  const [fading,    setFading]    = useState(false);
  const timerRef = useRef(null);

  const jumpTo = useCallback((idx) => {
    setFading(true);
    setTimeout(() => { setSlide(idx); setFading(false); }, 300);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => jumpTo((idx + 1) % HERO_SLIDES.length), 5000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => setSlide(s => { setFading(false); return (s + 1) % HERO_SLIDES.length; }), 300);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, []);

  const manualSlide = useCallback((idx) => {
    clearInterval(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setSlide(idx);
      setFading(false);
      timerRef.current = setInterval(() => {
        setFading(true);
        setTimeout(() => setSlide(s => { setFading(false); return (s + 1) % HERO_SLIDES.length; }), 300);
      }, 5000);
    }, 300);
  }, []);

  // ── Hotels from backend ────────────────────────────────────────────────
  const [hotels,        setHotels]        = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(true);
  const hotelsRowRef = useRef(null);

  useEffect(() => {
    fetch('/api/hotels?status=active&limit=50')
      .then(r => r.json())
      .then(data => {
        const listings = data?.roomListings || [];
        // Group room-type rows into unique hotel objects (same transform as BookingPageNew)
        const map = new Map();
        listings.forEach(l => {
          if (!map.has(l.hotel_id)) {
            map.set(l.hotel_id, {
              hotelId:     l.hotel_id,
              hotelName:   l.hotel_name,
              city:        l.city        || '',
              state:       l.state       || '',
              country:     l.country     || '',
              category:    l.category    || '',
              description: l.hotel_description || '',
              minPrice:    l.price       || 0,
              maxPrice:    l.price       || 0,
              roomTypes:   [],
              facilities:  Array.isArray(l.room_facilities) ? l.room_facilities.filter(Boolean) : [],
            });
          }
          const h = map.get(l.hotel_id);
          h.roomTypes.push({ id: l.room_type_id, name: l.room_type, capacity: l.capacity, price: l.price, available: l.available_rooms_count });
          h.minPrice = Math.min(h.minPrice, l.price || 0);
          h.maxPrice = Math.max(h.maxPrice, l.price || 0);
        });
        setHotels(Array.from(map.values()));
      })
      .catch(() => setHotels([]))
      .finally(() => setHotelsLoading(false));
  }, []);

  // Auto-scroll hotels
  useEffect(() => {
    const row = hotelsRowRef.current;
    if (!row || hotels.length === 0) return;
    const tick = setInterval(() => {
      if (row.scrollLeft + row.clientWidth >= row.scrollWidth - 10) {
        row.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        row.scrollBy({ left: 280, behavior: 'smooth' });
      }
    }, 3200);
    return () => clearInterval(tick);
  }, [hotels]);

  // ── Real Google-Places photo per destination ─────────────────────────────
  // The DESTINATIONS array carries Unsplash fallbacks; once mounted we fetch
  // a real, current photo of each city from /api/travel/public/destination-photo
  // (same Google Places source as the attractions tab) and overlay it. Failed
  // fetches keep the Unsplash fallback so the carousel never goes blank.
  const [destPhotos, setDestPhotos] = useState({}); // { 'Dubai': 'https://...' }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        DESTINATIONS.map(async (d) => {
          try {
            const q = `${d.name}, ${d.country}`;
            const r = await fetch(`/api/travel/public/destination-photo?destination=${encodeURIComponent(q)}`);
            if (!r.ok) return [d.name, null];
            const j = await r.json();
            return [d.name, j?.photoUrl || null];
          } catch {
            return [d.name, null];
          }
        })
      );
      if (cancelled) return;
      setDestPhotos(Object.fromEntries(results.filter(([, url]) => url)));
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Attractions ────────────────────────────────────────────────────────
  const [attrDest,    setAttrDest]    = useState(0);
  const [attractions, setAttractions] = useState([]);
  const [attrLoading, setAttrLoading] = useState(true);
  const attrCache = useRef({});

  useEffect(() => {
    const dest = ATTR_DESTINATIONS[attrDest];
    if (!dest) return;
    if (attrCache.current[dest.query]) {
      setAttractions(attrCache.current[dest.query]);
      setAttrLoading(false);
      return;
    }
    setAttrLoading(true);
    setAttractions([]);
    fetch(`/api/travel/public/attractions?destination=${encodeURIComponent(dest.query)}&limit=8`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (!data?.success) throw new Error(data?.message || 'API error');
        const list = Array.isArray(data.data) ? data.data : [];
        attrCache.current[dest.query] = list;
        setAttractions(list);
      })
      .catch(err => {
        console.error('[Attractions] fetch failed:', err.message);
        setAttractions([]);
      })
      .finally(() => setAttrLoading(false));
  }, [attrDest]);

  // ── Search submit ──────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams({ destination, checkIn, checkOut, guests: String(guests) });
    navigate(`/booking?${p.toString()}`);
  };

  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className="hp-page">

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="hp-hero">
        {HERO_SLIDES.map((s, i) => (
          <div
            key={i}
            className={`hp-hero-bg ${i === slide ? 'hp-hero-bg--active' : ''}`}
            style={{ backgroundImage: `url(${s.url})` }}
          />
        ))}
        <div className="hp-hero-overlay" />

        <div className={`hp-hero-content ${fading ? 'hp-hero-content--fade' : ''}`}>
          <h1 className="hp-hero-title">{HERO_SLIDES[slide].title}</h1>
          <p className="hp-hero-sub">{HERO_SLIDES[slide].subtitle}</p>

          <form className="hp-search-bar" onSubmit={handleSearch}>
            <div className="hp-sf hp-sf--dest">
              <MapPin size={17} className="hp-sf-icon" />
              <input
                type="text"
                placeholder="Where are you going?"
                value={destination}
                onChange={e => setDestination(e.target.value)}
                className="hp-sf-input"
              />
            </div>

            <div className="hp-sf">
              <Calendar size={15} className="hp-sf-icon" />
              <div className="hp-sf-lbl">Check-in</div>
              <input type="date" value={checkIn} min={today}
                onChange={e => setCheckIn(e.target.value)} className="hp-sf-date" />
            </div>

            <div className="hp-sf">
              <Calendar size={15} className="hp-sf-icon" />
              <div className="hp-sf-lbl">Check-out</div>
              <input type="date" value={checkOut} min={checkIn}
                onChange={e => setCheckOut(e.target.value)} className="hp-sf-date" />
            </div>

            <div className="hp-sf hp-sf--guests">
              <Users size={15} className="hp-sf-icon" />
              <div className="hp-sf-lbl">Guests</div>
              <select value={guests} onChange={e => setGuests(Number(e.target.value))} className="hp-sf-select">
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>{n} {n === 1 ? 'adult' : 'adults'}</option>
                ))}
              </select>
            </div>

            <button type="submit" className="hp-search-btn">
              <Search size={17} /><span>Search</span>
            </button>
          </form>
        </div>

        <button className="hp-carr-btn hp-carr-btn--L"
          onClick={() => manualSlide((slide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}>
          <ChevronLeft size={20} />
        </button>
        <button className="hp-carr-btn hp-carr-btn--R"
          onClick={() => manualSlide((slide + 1) % HERO_SLIDES.length)}>
          <ChevronRight size={20} />
        </button>

        <div className="hp-carr-dots">
          {HERO_SLIDES.map((_, i) => (
            <button key={i} className={`hp-carr-dot ${i === slide ? 'hp-carr-dot--on' : ''}`}
              onClick={() => manualSlide(i)} />
          ))}
        </div>
      </section>

      {/* ── TRUST BAR ────────────────────────────────────────────────── */}
      <div className="hp-trust">
        {[['500K+', 'Happy guests'], ['120+', 'Premium hotels'], ['40+', 'Countries'], ['4.9 ★', 'Avg rating']].map(([num, lbl], i) => (
          <React.Fragment key={lbl}>
            {i > 0 && <span className="hp-trust-sep" />}
            <div className="hp-trust-item">
              <span className="hp-trust-num">{num}</span>
              <span className="hp-trust-lbl">{lbl}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* ── OUR HOTELS ───────────────────────────────────────────────── */}
      <section className="hp-section">
        <div className="hp-inner">
          <div className="hp-shead">
            <div>
              <h2 className="hp-stitle">Our Properties</h2>
              <p className="hp-ssub">Curated hotels available to book right now</p>
            </div>
            <button className="hp-see-all" onClick={() => navigate('/booking')}>
              View all <ArrowRight size={14} />
            </button>
          </div>

          {hotelsLoading ? (
            <div className="hp-hotels-row">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="hp-hotel-card hp-hotel-card--skeleton">
                  <div className="hp-hotel-img hp-skel" />
                  <div className="hp-hotel-body">
                    <div className="hp-skel hp-skel--title" />
                    <div className="hp-skel hp-skel--sub" />
                    <div className="hp-skel hp-skel--chip" />
                  </div>
                </div>
              ))}
            </div>
          ) : hotels.length === 0 ? (
            <div className="hp-hotels-empty">
              <BedDouble size={42} color="#9ca3af" />
              <p>No properties yet. <button className="hp-link" onClick={() => navigate('/booking')}>Browse</button></p>
            </div>
          ) : (
            <div className="hp-hotels-row" ref={hotelsRowRef}>
              {hotels.map((h, idx) => {
                const img   = HOTEL_IMGS[idx % HOTEL_IMGS.length];
                const price = h.minPrice > 0 ? formatPrice(h.minPrice) : null;
                const city  = [h.city, h.country].filter(Boolean).join(', ');
                const name  = h.hotelName || 'Property';
                const types = h.roomTypes?.length || 0;

                return (
                  <button key={h.hotelId || idx} className="hp-hotel-card"
                    onClick={() => navigate(`/property/${h.hotelId}`)}>
                    <div className="hp-hotel-img" style={{ backgroundImage: `url(${img})` }}>
                      {price && <span className="hp-hotel-price-badge">From {price}/night</span>}
                    </div>
                    <div className="hp-hotel-body">
                      <div className="hp-hotel-name">{name}</div>
                      {city && <div className="hp-hotel-city"><MapPin size={11} /> {city}</div>}
                      {types > 0 && (
                        <div className="hp-hotel-row" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {types} room type{types !== 1 ? 's' : ''}
                        </div>
                      )}
                      <div className="hp-amenities">
                        <AmenityChip type="wifi" />
                        <AmenityChip type="coffee" />
                        <AmenityChip type="parking" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── POPULAR DESTINATIONS ─────────────────────────────────────── */}
      <section className="hp-section hp-section--tint">
        <div className="hp-inner">
          <div className="hp-shead">
            <div>
              <h2 className="hp-stitle">Popular Destinations</h2>
              <p className="hp-ssub">Travellers' most loved places right now</p>
            </div>
            <button className="hp-see-all" onClick={() => navigate('/booking')}>
              Explore all <ArrowRight size={14} />
            </button>
          </div>

          <div className="hp-dest-grid">
            {DESTINATIONS.map(d => {
              const realPhoto = destPhotos[d.name];
              return (
                <button key={d.name} className="hp-dest-card"
                  onClick={() => navigate(`/booking?destination=${encodeURIComponent(d.name)}`)}>
                  <div className="hp-dest-img" style={{ backgroundImage: `url(${realPhoto || d.img})` }} />
                  <div className="hp-dest-info">
                    <div className="hp-dest-name">{d.name}</div>
                    <div className="hp-dest-country"><MapPin size={10} /> {d.country}</div>
                    <div className="hp-dest-tag">{d.tag}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── EXPLORE ATTRACTIONS ──────────────────────────────────────── */}
      <section className="hp-section hp-section--tint">
        <div className="hp-inner">
          <div className="hp-shead">
            <div>
              <h2 className="hp-stitle">
                <Compass size={22} style={{ verticalAlign: '-4px', marginRight: 8, color: '#0071c2' }} />
                Explore Attractions
              </h2>
              <p className="hp-ssub">Top-rated sights &amp; experiences around the world, powered by Google</p>
            </div>
          </div>

          {/* Destination tabs */}
          <div className="hp-attr-tabs">
            {ATTR_DESTINATIONS.map((d, i) => (
              <button
                key={d.query}
                className={`hp-attr-tab ${i === attrDest ? 'hp-attr-tab--active' : ''}`}
                onClick={() => setAttrDest(i)}
              >
                <span className="hp-attr-tab-flag">{d.flag}</span>
                {d.label}
              </button>
            ))}
          </div>

          {/* Cards */}
          {attrLoading ? (
            <div className="hp-attr-grid">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="hp-attr-card hp-attr-card--skeleton">
                  <div className="hp-attr-img hp-skel" />
                  <div className="hp-attr-body">
                    <div className="hp-skel hp-skel--title" style={{ marginBottom: 8 }} />
                    <div className="hp-skel hp-skel--sub" />
                  </div>
                </div>
              ))}
            </div>
          ) : attractions.length === 0 ? (
            <div className="hp-hotels-empty">
              <Compass size={38} color="#9ca3af" />
              <p>No attractions found for this destination.</p>
            </div>
          ) : (
            <div className="hp-attr-grid">
              {attractions.map((a) => (
                <div key={a.id} className="hp-attr-card" onClick={() => {
                  const dest = ATTR_DESTINATIONS[attrDest];
                  navigate(`/booking?destination=${encodeURIComponent(dest.label)}`);
                }}>
                  {a.photoUrl ? (
                    <div
                      className="hp-attr-img"
                      style={{ backgroundImage: `url(${a.photoUrl})` }}
                    />
                  ) : (
                    <div className="hp-attr-img hp-attr-img--placeholder">
                      <Compass size={38} color="rgba(255,255,255,0.5)" />
                    </div>
                  )}
                  <div className="hp-attr-overlay" />
                  <div className="hp-attr-body">
                    {a.type && (
                      <span className="hp-attr-type-badge">
                        {a.type.replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    )}
                    <div className="hp-attr-name">{a.name}</div>
                    {a.rating != null && (
                      <div className="hp-attr-rating">
                        <Star size={11} fill="#febb02" color="#febb02" />
                        <span>{a.rating.toFixed(1)}</span>
                        {a.reviewCount != null && (
                          <span className="hp-attr-reviews">
                            ({a.reviewCount.toLocaleString()})
                          </span>
                        )}
                      </div>
                    )}
                    {a.address && (
                      <div className="hp-attr-addr">
                        <MapPin size={10} />
                        {a.address.length > 42 ? a.address.slice(0, 42) + '…' : a.address}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────── */}      <section className="hp-section">
        <div className="hp-inner">
          <div className="hp-shead hp-shead--center">
            <div>
              <h2 className="hp-stitle">Why Choose InnBotics</h2>
              <p className="hp-ssub">Everything you need for the perfect stay — in one intelligent platform</p>
            </div>
          </div>
          <div className="hp-feat-grid">
            {FEATURES.map(({ Icon, color, title, desc }) => (
              <div key={title} className="hp-feat-card">
                <div className="hp-feat-icon" style={{ background: `${color}18`, color }}>
                  <Icon size={28} strokeWidth={1.8} />
                </div>
                <h3 className="hp-feat-title">{title}</h3>
                <p className="hp-feat-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ───────────────────────────────────────────────── */}
      <section className="hp-cta">
        <div className="hp-cta-inner">
          <h2 className="hp-cta-title">Ready to find your perfect stay?</h2>
          <p className="hp-cta-sub">Join 500,000+ happy travellers who book smarter with InnBotics</p>
          <div className="hp-cta-btns">
            <button className="hp-cta-primary" onClick={() => navigate('/booking')}>Browse Hotels</button>
            <button className="hp-cta-outline" onClick={() => navigate('/signup')}>Create Free Account</button>
          </div>
        </div>
      </section>

    </div>
  );
}
